/**
 * "My day" strings (`myDay.*`), merged into the dictionary through `extra.ts`.
 * `myDayEn` is typed against `myDayPl`, so a missing key is a compile error.
 */
type Shape<T> = { [K in keyof T]: T[K] extends string ? string : Shape<T[K]> };

export const myDayPl = {
  greeting: 'Dzień dobry, {name}',
  subtitle: 'Twój plan na dziś: zadania, alerty i postęp targetu.',
  scopeSubtitle: 'Widok zbiorczy: {label}',
  userTitle: 'Dzień: {name}',
  picker: {
    label: 'Pokaż',
    team: 'Cały zespół',
    all: 'Cała organizacja',
  },
  loadError: 'Nie udało się wczytać planu dnia.',
  loadErrorHint: 'Sprawdź połączenie i spróbuj ponownie.',
  hero: {
    sales: 'Sprzedaż MTD',
    target: 'Realizacja celu',
    forecast: 'Prognoza końca miesiąca',
    todo: 'Do zrobienia dziś',
    targetOf: 'cel {target}',
    forecastOf: '{pct} celu',
    todoHint: '{overdue} zaległych · {visits} wizyt',
    insightBehind: 'Prognoza {pct} celu — przy obecnym tempie zabraknie {amount}. Potrzebujesz {required}/dzień, a masz {avg}.',
    insightOnTrack: 'Cel w zasięgu: prognoza {pct} celu. Utrzymaj tempo {avg}/dzień.',
    insightReached: 'Cel miesiąca osiągnięty — każda kolejna sprzedaż to nadwyżka.',
    insightOverdue: '{count} zaległych zadań czeka na zamknięcie.',
    insightDefault: 'Wszystko pod kontrolą — zajrzyj do alertów i zaplanowanych wizyt.',
    seeCustomers: 'Klienci ze spadkiem',
    seeTasks: 'Zobacz zadania',
    seeAlerts: 'Alerty',
    actions: { tasks: 'Moje zadania', customers: 'Klienci', newCustomer: 'Nowy klient', leads: 'Leady' },
  },
  kpi: {
    sales: 'Sprzedaż MTD',
    vsPrev: 'vs poprz. mies.',
    margin: 'marża {pct}',
    target: 'Realizacja targetu',
    targetOf: 'target {target}',
    remaining: 'Pozostało {amount}',
    targetReached: 'Target osiągnięty',
    noTarget: 'Brak targetu',
    noTargetHint: 'Target na ten miesiąc nie został ustalony.',
    forecast: 'Prognoza końca miesiąca',
    forecastVsTarget: '{pct} targetu',
    forecastNoTarget: 'Bez porównania z targetem',
    required: 'Wymagana sprzedaż/dzień',
    avgDaily: 'obecnie {amount} / dzień',
    workingDays: 'dni roboczych: {left} z {total}',
    requiredDone: 'Target osiągnięty',
  },
  counts: {
    tasksToday: 'Zadania dziś',
    tasksOverdue: 'Zaległe',
    visitsToday: 'Wizyty dziś',
    toContact: 'Do kontaktu',
    declining: 'Ze spadkiem',
    newLeads: 'Nowe leady',
  },
  alerts: {
    title: 'Alerty',
    all: 'Wszystkie alerty',
    empty: 'Brak otwartych alertów',
    emptyBody: 'Nic nie wymaga teraz Twojej uwagi.',
  },
  tasks: {
    title: 'Zadania na dziś',
    all: 'Wszystkie zadania',
    empty: 'Brak zadań na dziś',
    emptyBody: 'Masz wolny kalendarz — dobry moment na kontakt z klientami.',
    overdueCount: '{count} zaległych',
  },
  visits: {
    title: 'Plan wizyt',
    subtitle: 'Dzisiejsze wizyty u klientów',
    all: 'Wszystkie wizyty',
    empty: 'Brak zaplanowanych wizyt',
    emptyBody: 'Na dziś nie zaplanowano żadnej wizyty.',
    noTime: 'bez godziny',
    status: {
      PLANNED: 'Zaplanowana',
      IN_PROGRESS: 'W trakcie',
      COMPLETED: 'Zakończona',
      CANCELLED: 'Anulowana',
      MISSED: 'Nieodbyta',
    },
  },
  declining: {
    title: 'Klienci ze spadkiem',
    subtitle: 'Największy spadek sprzedaży',
    empty: 'Brak klientów ze spadkiem',
    emptyBody: 'Sprzedaż u Twoich klientów jest stabilna.',
    open: 'Otwórz kartę klienta',
  },
  weekly: {
    title: 'Sprzedaż tygodniowa',
    subtitle: 'Netto, ostatnie 12 tygodni',
    series: 'Sprzedaż netto',
    empty: 'Brak danych o sprzedaży',
    weekOf: 'Tydzień od {date}',
  },
};

export type MyDayMessages = Shape<typeof myDayPl>;

export const myDayEn: MyDayMessages = {
  greeting: 'Good morning, {name}',
  subtitle: 'Your plan for today: tasks, alerts and target progress.',
  scopeSubtitle: 'Aggregated view: {label}',
  userTitle: 'Day of {name}',
  picker: {
    label: 'Show',
    team: 'Whole team',
    all: 'Whole organization',
  },
  loadError: 'Could not load your day.',
  loadErrorHint: 'Check your connection and try again.',
  hero: {
    sales: 'Sales MTD',
    target: 'Target attainment',
    forecast: 'Month-end forecast',
    todo: 'To do today',
    targetOf: 'target {target}',
    forecastOf: '{pct} of target',
    todoHint: '{overdue} overdue · {visits} visits',
    insightBehind: 'Forecast is {pct} of target — at the current pace you will be {amount} short. You need {required}/day, you have {avg}.',
    insightOnTrack: 'Target within reach: forecast {pct} of target. Keep the pace of {avg}/day.',
    insightReached: 'Monthly target reached — every further sale is upside.',
    insightOverdue: '{count} overdue tasks are waiting to be closed.',
    insightDefault: 'All under control — check alerts and planned visits.',
    seeCustomers: 'Declining customers',
    seeTasks: 'See tasks',
    seeAlerts: 'Alerts',
    actions: { tasks: 'My tasks', customers: 'Customers', newCustomer: 'New customer', leads: 'Leads' },
  },
  kpi: {
    sales: 'Sales MTD',
    vsPrev: 'vs last month',
    margin: 'margin {pct}',
    target: 'Target attainment',
    targetOf: 'target {target}',
    remaining: '{amount} to go',
    targetReached: 'Target reached',
    noTarget: 'No target',
    noTargetHint: 'No target has been set for this month.',
    forecast: 'End-of-month forecast',
    forecastVsTarget: '{pct} of target',
    forecastNoTarget: 'No target to compare with',
    required: 'Required daily sales',
    avgDaily: 'currently {amount} / day',
    workingDays: 'working days: {left} of {total}',
    requiredDone: 'Target reached',
  },
  counts: {
    tasksToday: 'Tasks today',
    tasksOverdue: 'Overdue',
    visitsToday: 'Visits today',
    toContact: 'To contact',
    declining: 'Declining',
    newLeads: 'New leads',
  },
  alerts: {
    title: 'Alerts',
    all: 'All alerts',
    empty: 'No open alerts',
    emptyBody: 'Nothing needs your attention right now.',
  },
  tasks: {
    title: "Today's tasks",
    all: 'All tasks',
    empty: 'No tasks for today',
    emptyBody: 'A clear calendar — a good moment to reach out to customers.',
    overdueCount: '{count} overdue',
  },
  visits: {
    title: 'Visit plan',
    subtitle: "Today's customer visits",
    all: 'All visits',
    empty: 'No visits planned',
    emptyBody: 'No visit is planned for today.',
    noTime: 'no time set',
    status: {
      PLANNED: 'Planned',
      IN_PROGRESS: 'In progress',
      COMPLETED: 'Completed',
      CANCELLED: 'Cancelled',
      MISSED: 'Missed',
    },
  },
  declining: {
    title: 'Declining customers',
    subtitle: 'Biggest sales drop',
    empty: 'No declining customers',
    emptyBody: 'Sales across your customers are stable.',
    open: 'Open customer card',
  },
  weekly: {
    title: 'Weekly sales',
    subtitle: 'Net, last 12 weeks',
    series: 'Net sales',
    empty: 'No sales data',
    weekOf: 'Week of {date}',
  },
};
